/**
 * Pure derivations for the Performance tab. No fetching, no business arithmetic.
 *
 * WHAT "NO BUSINESS ARITHMETIC" MEANS HERE
 * ----------------------------------------
 * Every figure this tab shows is computed by the backend and arrives finished.
 * Nothing in this file adds, subtracts or compares two of them: there is no
 * month-over-month delta, no percentage change and no re-derived margin. The one
 * division that does happen — a room-day share for a bar's width — divides two
 * numbers from the SAME month that the contract already publishes as parts of
 * one whole, and its result never appears as a figure: the values beside the bar
 * are the counts themselves.
 *
 * That distinction is the whole reason this file exists rather than a few inline
 * helpers. A helper that turns `selected` and `previous` into "+4.4 %" would look
 * like formatting and would be a second definition of growth, computed in a
 * browser, next to figures the server defines exactly.
 *
 * MONEY IS A DECIMAL STRING
 * -------------------------
 * Pydantic serializes Decimal as JSON text with the scale the arithmetic
 * produced — `"2400.0"`, `"1500.55"`. `typeof x === "number"` is false for every
 * one of them, which is why `toNumber` is the gate rather than a typeof check,
 * and why `money`/`pct` live here instead of being re-invented per card.
 */
import { toNumber } from "../companyOverview/overviewSelectors";
import { chf } from "../companyFinance/financeFormat";
import { formatPercent } from "../../../pages/admin/uebersicht/uebersichtHelpers";

/**
 * A franc figure for DISPLAY: "—" when absent, formatted when present.
 *
 * The distinction is load-bearing in both directions. An absent figure must read
 * as a dash rather than "CHF 0", because "we have no number" and "the number is
 * zero" are different claims — and an EXPLICIT `"0.0"` must stay a zero, because
 * a unit that genuinely earned nothing is information. `toNumber` is the app's
 * existing rule for exactly that; `chf` is the finance surface's formatter, so
 * one CHF format holds across the company tabs.
 */
export function money(value) {
  const n = toNumber(value);
  return n === null ? "—" : chf(n);
}

/**
 * A percentage for DISPLAY, to one decimal — the format this surface has always
 * used ("19.6%"). `formatPercent` maps null to "0.0%", so the null decision
 * happens here, before it: a margin that does not exist is not a margin of zero.
 */
export function pct(value) {
  const n = toNumber(value);
  return n === null ? "—" : formatPercent(n);
}

/**
 * The row `worst_unit_id` names — or nothing at all.
 *
 * Fail-closed on purpose. If the id is absent, or names a unit the list does not
 * contain, this returns `null` and the card says it has no data. The alternative
 * (falling back to the last array element) is precisely the legacy behaviour S4
 * replaced: it always shows SOMETHING, including when the server's answer and
 * the array disagree, and it is then showing a unit the server did not choose.
 */
export function selectWorstUnit(ranking) {
  const items = Array.isArray(ranking?.items) ? ranking.items : [];
  const worstId = ranking?.worst_unit_id;
  if (worstId == null) return null;
  return items.find((item) => item && item.unit_id === worstId) || null;
}

/** The ranking rows, in the server's order, or an empty list. Never sorted. */
export function selectRankingItems(ranking) {
  return Array.isArray(ranking?.items) ? ranking.items : [];
}

/** The units of one month, in the contract's own order. Never sorted, never sliced. */
export function selectUnits(period) {
  return Array.isArray(period?.units) ? period.units : [];
}

/**
 * At most two short statements for the insight banner, taken from the wire.
 *
 * The banner is a SUMMARY of the methodology, not a second copy of it: it picks
 * the first paragraph of the two sections a reader most needs before reading any
 * figure — how revenue is prorated, and that the result is not a company profit
 * — and the full text stays in the popover. Nothing is authored here; if the
 * wire carries no methodology the banner does not render at all, because an
 * invented sentence about a computation is worse than no sentence.
 */
export function selectInsightStatements(methodology) {
  if (!methodology) return [];
  const pick = (section) => {
    const paragraphs = Array.isArray(section?.paragraphs) ? section.paragraphs : [];
    const first = paragraphs.find((p) => typeof p === "string" && p.trim().length);
    return first ? { title: section.title, text: first.trim() } : null;
  };
  // Two candidates in, so "at most two" needs no cap — and adding one would put
  // a `.slice(` in this folder, which the structural guard bans outright so that
  // it can never appear on a server-ordered list without someone noticing.
  return [pick(methodology.revenue), pick(methodology.result)].filter(Boolean);
}

/**
 * One month's room-day split, as parts of one whole.
 *
 * `available_room_days` is the denominator the contract publishes; occupied and
 * vacant are its parts. The shares exist only to give two bar segments a width
 * and are clamped into [0, 1] — a share is never printed, and the counts beside
 * the bar are the actual figures. When there is no denominator (a month with no
 * available room-days) `ratio` is null and the caller draws no bar rather than a
 * full or empty one, either of which would be a claim.
 */
export function selectOccupancySplit(period) {
  const available = toNumber(period?.available_room_days);
  const occupied = toNumber(period?.occupied_room_days);
  const vacant = toNumber(period?.vacant_room_days);
  const share = (part) =>
    available && available > 0 && part !== null
      ? Math.min(1, Math.max(0, part / available))
      : null;
  return {
    available,
    occupied,
    vacant,
    occupiedShare: share(occupied),
    vacantShare: share(vacant),
    occupancyPct: toNumber(period?.monthly_occupancy_pct),
  };
}

/**
 * Neutral disclosure lines for the two data-quality signals this tab surfaces.
 *
 * ONLY WHEN THERE IS SOMETHING TO SAY. A zero signal produces no line, so the
 * tab never shows an empty warning area — "0 Kostenzeilen ohne Kategorie" is
 * noise that trains a reader to ignore the whole block.
 *
 * NO SEVERITY. The contract publishes raw counts and deliberately no severity,
 * no threshold and no wording; inventing "kritisch" here would be this layer
 * making a judgement no service stands behind. Two signals only — the
 * unclassified cost rows, because they are the visible difference between this
 * result and the legacy raw total, and the revenue rows that reached no unit,
 * because they are money the population gate left outside these figures.
 */
export function selectDataQualityNotices(dataQuality) {
  const notices = [];
  const unclassified = toNumber(dataQuality?.costs?.unclassified_count);
  if (unclassified !== null && unclassified > 0) {
    notices.push({
      key: "unclassified-costs",
      text:
        unclassified === 1
          ? "1 Kostenzeile ohne Kategorie – nicht im Objektergebnis enthalten."
          : `${unclassified} Kostenzeilen ohne Kategorie – nicht im Objektergebnis enthalten.`,
    });
  }
  const unassigned = toNumber(dataQuality?.population?.revenue_unassigned_tenancy_count);
  if (unassigned !== null && unassigned > 0) {
    notices.push({
      key: "revenue-unassigned",
      text:
        unassigned === 1
          ? "1 Mietverhältnis ohne zugeordnete Einheit – nicht in der Einheiten-Aufstellung enthalten."
          : `${unassigned} Mietverhältnisse ohne zugeordnete Einheit – nicht in der Einheiten-Aufstellung enthalten.`,
    });
  }
  return notices;
}
